const DASH_PREFIX = 'dashboard';
const DASH_VERSION = 'v1';
const AUTH_PREFIX = 'auth';
const AUTH_VERSION = 'v1';
const trim = (s: string) => s?.replace(/\/+$/, '');
const lead = (p = '') => (p.startsWith('/') ? p : `/${p}`);

const dashHost = trim(import.meta.env['VITE_API_BASE_URL']);
const authHost = trim(import.meta.env['VITE_AUTH_BASE_URL']);
const wsHost = trim(import.meta.env['VITE_WS_BASE_URL']);

export const dashUrl = (p = '') => `${dashHost}/${DASH_PREFIX}/${DASH_VERSION}${lead(p)}`;

export const authUrl = (p = '') => `${authHost}/${AUTH_PREFIX}/${AUTH_VERSION}${lead(p)}`;

export const wsUrl = (p = '') => `${wsHost}/${DASH_PREFIX}/${DASH_VERSION}${lead(p)}`;

const authUrls = {
	loginBeta: (code: string) => `/login/old/dashboard?code=${code}`,
	loginBetaRegenerateCode: (code: string) => `/login/retry/old/dashboard?code=${code}`,
	login: `/login`,
	loginFakeAccess: (company_id: number) => `/login/impersonate/${company_id}`,
	loginSwitchCredentials: `/login/switch-credentials`,
	loginAvailableDelegations: `/login/available-delegations`,
	loginPermissions: `/login/user/permissions-configuration`,
	userInfo: `/login/user/information`,
	signUp: `/password/reset/change`,
	resetPassword: `/password/reset/change`,
	forgotPassword: `/password/reset`,
	refresh: `/refresh`,
	changePassword: `/password/change`,
	logout: `/signout`,
} as const;

const dashboardUrls = {
	home: `/home`,
	pointOfSales: `/pos`,
	users: `/users`,
	settings: `/settings`,
	profile: `/profile`,
	customerService: `/customer-service`,
	finance: `/finance`,
	clients: `/clients`,
	marketing: `/marketing`,
	tables: `/table/configs`,
	notifications: `/notifications`,
	tags: `/tags`,
	alerts: `/alerts`,
	addons: `/addons`,
	marketplace: '/marketplace',
	roles: '/roles',
} as const;

const getBoxUrl = (group: string, cardEndpoint: string) => `/${group}/box/${cardEndpoint}`;

/* Finance API URLs */
const financePrefix = dashboardUrls.finance;

const financeUrls = {
	chart: `${financePrefix}/chart/sales_total`,
	tableTransactions: `${financePrefix}/table/sales_activity`,
	transactionDetails: (id: number, transactionType: string) =>
		`${financePrefix}/form/transaction_info/${id}?id=${id}&transaction_type=${transactionType}`,
	refundTransaction: (reference: string) => `${financePrefix}/transaction/${reference}/refund`,
	financeTable: `${financePrefix}/activity/table`,
	conciliationsTable: `${financePrefix}/table/conciliation`,
	boxesConciliations: `${financePrefix}/boxes/conciliation`,
	conciliationDetailsTable: `${financePrefix}/table/conciliation/details`,
	downloadConciliationDetails: `${financePrefix}/download/conciliation/details`,
	downloadConciliationPeriod: `${financePrefix}/download/conciliation/period`,
	conciliationDetailBox: `${financePrefix}/boxes/conciliation/details`,
	totalSalesBox: `sales`,
	refundsBox: `refunds`,
	topUps: `recharges`,
	avgCostPerTransactionBox: `average_cost_per_transaction`,

	// charts for home page
	totalSalesChart: `${financePrefix}/chart/total-sales`,
	paymentMethodsChart: `${financePrefix}/chart/payment-methods`,
	errorsPaymentMethodsChart: `${financePrefix}/chart/errors-payment-methods`,

	acceptanceRate: `${financePrefix}/box/acceptance-rate`,
	successfullSales: `${financePrefix}/box/exit-sales`,
};

/* Customer Service */

const customerServicePrefix = dashboardUrls.customerService;

const customerServiceUrls = {
	chat: (ticket_id: number) => `${customerServicePrefix}/chat/${ticket_id}`,
	userHistorical: `${customerServicePrefix}/table/tickets/user_historical`,
	userTicketsHistory: `${customerServicePrefix}/table/tickets/user_historical`,
	ticket_chat: (ticket_id: string) => `${customerServicePrefix}/chat/${ticket_id}`,
	chat_actions: `${customerServicePrefix}/chat/button/action`,
	ticketsTable: `${customerServicePrefix}/table/tickets`,

	orainConversation: (id: number) => `${customerServicePrefix}/conversation/${id}`,
	orainConversationReply: (id: number) => `${customerServicePrefix}/conversation/${id}/reply`,
	orainUserTicketsHistory: `${customerServicePrefix}/table/tickets/user_historical/orain`,
	chatActionsOrain: `${customerServicePrefix}/chat/button/action/orain`,

	// BOXES
	newTickets: 'tickets/new',
	pendingTickets: 'tickets/pending',
	onHoldTickets: 'tickets/on_hold',
	ticket: `${customerServicePrefix}/ticket`,
	submitChatAction: `${customerServicePrefix}/chat/button/action`,
};

/* Points of sale */
const posPrefix = dashboardUrls.pointOfSales;

const pointsOfSaleUrls = {
	productsTable: `${posPrefix}/table/pos/products`,
	getPointsOfSaleDetails: (id: string) => `${posPrefix}/pos/information?id_machine=${id}`,
	assignMachine: `${posPrefix}/machine`,
	editPointsOfSaleDetails: (id: string) => `${posPrefix}/machine?id_machine=${id}`,
	inhibitSchedule: `${posPrefix}/machine/inhibit-schedule`,
	deletePointsOfSale: (id: string) => `${posPrefix}/pos/machine/${id}`,

	posProductsCategories: `${posPrefix}/products/product_categories`,
	crudRail: `${posPrefix}/machine/rail`,
	syncPlanogram: `${posPrefix}/sync/planogram`,
	createProduct: `${posPrefix}/product`,

	posTable: `${posPrefix}/table/pos`,
	restartMachine: `${posPrefix}/machine/restart`,
	deleteMachine: `${posPrefix}/machine`,
	evaDts: `${posPrefix}/eva-dts`,
	evaDtsTable: `${posPrefix}/table/eva-dts`,
	sendVirtualCredit: `${posPrefix}/machine/send_virtual_machine`,

	posConnectivityTable: `${posPrefix}/table/pos-connectivity`,

	productTable: `${posPrefix}/products/table`,
	crudProduct: `${posPrefix}/product`,

	stockTable: `/stock/table`,
	stockProductsTable: `/stock/table/products`,
	withStockBox: `/stock/box/full`,
	lowStockBox: `/stock/box/low`,
	outOfStockBox: `/stock/box/out`,

	planogramTable: `${posPrefix}/pos/planogram/table`,

	connectivityChart: `${posPrefix}/pos/connectivity/chart`,
	connectivityTable: `${posPrefix}/pos/connectivity/table`,

	//activity
	posActivityTable: `${posPrefix}/pos/activity/table`,
	posActivityBoxApp: `${posPrefix}/pos/box/activity/app`,
	posActivityBoxTag: `${posPrefix}/pos/box/activity/tag`,
	posActivityBoxCash: `${posPrefix}/pos/box/activity/cash`,
	posActivityBoxCard: `${posPrefix}/pos/box/activity/card`,

	posCatalog: `${posPrefix}/catalog`,
	posCatalogTable: `${posPrefix}/catalog/table`,
	devicesTable: `${posPrefix}/devices/table`,
	resetDevice: `${posPrefix}/reset/device`,
	updateFirmware: `${posPrefix}/update/firmware`,

	refillStock: `${posPrefix}/refill/stock`,

	//boxes
	withSalesBox: `with_sales`,
	averageSalesBox: `average_sales`,
	averageTransactionBox: `average_transactions`,
	averagePurchasesBox: `average_purchase`,
	machinesWithoutIncidents: `${posPrefix}/box/machine-without-incidents`,
	machineConectivity: `${posPrefix}/box/machine-connectivity`,

	masterConfig: `${posPrefix}/master-configuration`,
	favorites: `${posPrefix}/favorites`,
	transactionSummary: `${posPrefix}/chart/point-of-sales-activity`,
};

const irpUrls = {
	irpTable: `${dashboardUrls.pointOfSales}/table/irp`,
	crudIrp: `${dashboardUrls.pointOfSales}/irp_configuration`,
	getConfigIrp: `${dashboardUrls.pointOfSales}/irp_configuration`,
};

/* Clients API Urls */
const clientsPrefix = dashboardUrls.clients;
const clientsUrls = {
	crudClient: `${clientsPrefix}`,
};

/* Settings */

const settingsPrefix = dashboardUrls.settings;
const rolesPrefix = dashboardUrls.roles;

const settingsUrls = {
	/* Fiscal data */
	getBillingInfo: `${settingsPrefix}/fiscal_data/billing_information`,
	billingTable: `${settingsPrefix}/fiscal_data/billing/table`,
	getShippingInfo: `${settingsPrefix}/fiscal_data/shipping_information`,
	updateBillingInfo: `${settingsPrefix}/fiscal_data/shipping`,

	/* ERP Configuration */
	getErpCofig: `${settingsPrefix}/erp_configuration/erp_information`,
	updateErpConfig: `${settingsPrefix}/erp_configuration/erp`,
	createErpConfig: `${settingsPrefix}/erp_configuration/erp`,

	/* Roles */
	getRoles: `${settingsPrefix}/roles/table/`,
	createUserRole: `${settingsPrefix}/roles/add_user_role`,
	updateUserRole: `${settingsPrefix}/roles/update_user_role`,
	deleteUserRole: `${settingsPrefix}/roles/delete_user`,
	rolesTable: `${settingsPrefix}/roles/table`,
	sendRoleEmail: `${settingsPrefix}/roles/renew/mail`,
	resetRolePassword: `${settingsPrefix}/roles/password/reset`,
	getCompanyRoles: `${rolesPrefix}/by_company`,
	roleCatalog: `${rolesPrefix}/catalog`,
	createCustomRole: `${rolesPrefix}/create_custom_role`,
	deleteCustomRole: (roleId: number) => `${rolesPrefix}/${roleId}`,
	updateCustomRole: (roleId: number) => `${rolesPrefix}/${roleId}`,

	/* Client dashboard */
	getTableDelegation: `${settingsPrefix}/clients/table/`,
	getDelegationDetails: (id: string) => `${settingsPrefix}/clients/clients_information?id=${id}`,
	crudDelegation: `${settingsPrefix}/clients/delegation`,
	delegationTable: `${settingsPrefix}/clients/table`,
	delegationMachineTable: `${settingsPrefix}/clients/delegation/machines/table`,
	delegationMachine: `${settingsPrefix}/clients/delegation/machines`,

	/* General Configuration */
	getGeneralConfig: `${settingsPrefix}/general_configuration/configuration`,
	getCurrencyConfig: `${settingsPrefix}/company-exchange`,
	getRefundsConfig: `${settingsPrefix}/company-auto-refund`,
	updateGeneralConfig: `${settingsPrefix}/general_configuration`,
	updateCurrencyConfig: `${settingsPrefix}/company-exchange`,
	updateRefundsConfig: `${settingsPrefix}/company-auto-refunds`,

	/* Technicians */
	createTechnician: `${settingsPrefix}/technical/add`,
	deleteTechnician: `${settingsPrefix}/technical/delete`,
};

/* Profile */
const profilePrefix = dashboardUrls.profile;

const profileUrls = {
	getProfile: `${profilePrefix}/profile_info`,
	updateProfile: `${profilePrefix}/profile_info`,
	changeAvatar: `${profilePrefix}/avatar`,
	getIdandClientDashboard: `/login/info_user`,
};

/* Marketing */
const marketingPrefix = dashboardUrls.marketing;

const marketingUrls = {
	marketingBox: (boxEndpoint: string) => `${marketingPrefix}/box/${boxEndpoint}`,
	getChartSalesByPos: `${marketingPrefix}/chart/sales_avg_by_pos`,
	getChartTotalSales: `${marketingPrefix}/chart/sales`,
	getTableTotalSales: `${marketingPrefix}/table/promotions`,
	getPromotionInfo: (promoId: string) =>
		`${marketingPrefix}/promotion/info?promotion_id=${promoId}`,
	updatePromotion: (promoId: number) => `${marketingPrefix}/promotion?promotion_id=${promoId}`,
	promotionTable: `${marketingPrefix}/table`,
	getPromotionActivityTable: `${marketingPrefix}/table/activity-promotion`,
	getPromotionTargetTable: `${marketingPrefix}/table/users-promotion`,
	activateToggle: `${marketingPrefix}/promotion/toggle`,
	crudPromotion: `${marketingPrefix}/promotion`,
	prioritizePromotions: `${marketingPrefix}/promotion/priority`,
	createPromotionV2: `${marketingPrefix}/promotion/v2`,
	promotionV2: (promotionId: string) => `${marketingPrefix}/promotion/v2/${promotionId}`,
	deletePromotionV2: `${marketingPrefix}/promotion/v2`,
};

/* Users */

const usersPrefix = dashboardUrls.users;

const usersUrls = {
	userDetails: `${usersPrefix}/information`,
	allUsersTable: `${usersPrefix}/table`,
	userActivityTable: `${usersPrefix}/table/activity`,
	activeUsersBox: `${usersPrefix}/box/active-users`,
	expiredVouchersBox: `${usersPrefix}/box/expired-vouchers`,
	purchasesWithPromotionBox: `${usersPrefix}/box/purchase-with-promotions`,
	rechargesBox: `${usersPrefix}/box/recharges`,
	usersPaymentTokens: `${usersPrefix}/payment/tokens`,
	sendVirtualCredit: `${usersPrefix}/send_virtual_credit`,

	// users boxes
	userBoxPurchases: `purchases`,
	userBoxRefunds: `refunds`,
	userBoxRecharges: `recharges`,
};

/* Tags */

const tagsPrefix = dashboardUrls.tags;
const tagsUrls = {
	activeTagsBox: `${tagsPrefix}/box/active-tags`,
	expiredVouchersBox: `${tagsPrefix}/box/expired-vouchers`,
	purchasesWithPromotionBox: `${tagsPrefix}/box/purchase-with-promotions`,
	rechargesBox: `${tagsPrefix}/box/recharges`,

	tagsTable: `${tagsPrefix}/table`,
	tagsWhitelistTable: `${tagsPrefix}/table/whitelist`,
	tagsWhitelistsTable: `${tagsPrefix}/table/whitelists`,
	tagActivityTable: `${tagsPrefix}/table/activity`,

	tagInfo: `${tagsPrefix}/information`,

	addTag: `${tagsPrefix}/add`,
	blockUblockTag: `${tagsPrefix}/block_unlock`,
	resetTagBalance: `${tagsPrefix}/reset`,
	transferTagBalance: `${tagsPrefix}/transfer_balance`,
	assignUnassignTag: `${tagsPrefix}/link_unlink`,
	renameTag: `${tagsPrefix}/rename`,
	addToWhitelist: `${tagsPrefix}/add/whitelist`,
	deleteFromWhitelist: `${tagsPrefix}/delete/whitelist`,
	deleteWhitelist: `${tagsPrefix}/delete/whitelist`,
	getTagsBalance: `${tagsPrefix}/balance`,
	deleteTags: `${tagsPrefix}/delete`,
	sendVirtualCredit: `${tagsPrefix}/send_virtual_credit`,
	analyzeCsv: `${tagsPrefix}/analyze-csv`,
};

const notificationsPrefix = dashboardUrls.notifications;
const notificationsUrls = {
	notification: (id: number) => `${notificationsPrefix}/notifications/${id}`,
};

/* Reports */
const reportsPrefix = `/report`;
const reportsUrls = {
	reportsTable: `${reportsPrefix}/history/table`,
	templatesTable: `${reportsPrefix}/catalog/table`,
	getConfiguration: `${reportsPrefix}/configuration`,
	downloadReport: `${reportsPrefix}/download/report`,
	renameTemplate: `${reportsPrefix}/rename`,
	deleteReport: `${reportsPrefix}/report`,
	createReport: `${reportsPrefix}/new-run`,
	crudReportsAutomation: (templateId?: number) =>
		`${reportsPrefix}/setup${templateId ? `/${templateId}` : ''}`,
	reportTypes: `${reportsPrefix}/reports-types`,
	reportFrequency: `${reportsPrefix}/enum/interval-units	`,
	getScheduledReport: (id: number) => `${reportsPrefix}/schedule/${id}`,
};

/*Form Options*/
const formOptionsPrefix = `/forms`;
const formOptionsUrls = {
	clients: `${formOptionsPrefix}/clients`,
	machines: `${formOptionsPrefix}/machines`,
	locations: `${formOptionsPrefix}/machines/locations`,
	products: `${formOptionsPrefix}/products`,
	users: `${formOptionsPrefix}/users`,
	showDeleted: `${formOptionsPrefix}/show_deleted`,
	categories: `${formOptionsPrefix}/products/categories`,
	catalogs: `${formOptionsPrefix}/catalogs`,
	roles: `${formOptionsPrefix}/roles`,
	coins: `${formOptionsPrefix}/coins`,
	tags: `${formOptionsPrefix}/tags`,
	promotions: `${formOptionsPrefix}/promotions`,
	promotionsActions: `${formOptionsPrefix}/promotions/actions`,
	promotionUsers: `${formOptionsPrefix}/promotions/users`,
	machinesTypes: `${formOptionsPrefix}/machines/types`,
	tagCsv: `${formOptionsPrefix}/tags/from/csv`,
	firmwareVersions: `${formOptionsPrefix}/devices/firmware`,
	machineActivationCodes: `${formOptionsPrefix}/machines/activation_codes`,
	machineServices: `${formOptionsPrefix}/machines/service`,
	companies: `${formOptionsPrefix}/companies`,
	delegations: `${formOptionsPrefix}/delegations`,
	routes: `${formOptionsPrefix}/machines/routes`,
	countries: `${formOptionsPrefix}/countries`,
	machinesBrands: `${formOptionsPrefix}/machines/brand`,
	machinesModels: `${formOptionsPrefix}/machines/model`,
	machinesFirmware: `${formOptionsPrefix}/machines/firmware`,
	emails: `${formOptionsPrefix}/mail-employees`,
	posPersonalizations: `${formOptionsPrefix}/pos-configurations`,
	addons: `${formOptionsPrefix}/addons`,
	refundReasons: `${formOptionsPrefix}/refund-reasons`,
};

/* Orain */
const orainUrls = {
	access: dashUrl('/orain/access'),
};

/* Tasks */
const tasksPrefix = dashUrl('tasks');
const tasksUrls = {
	taskStatus: `${tasksPrefix}/status`,
};

/* Tracking */
const trackingPrefix = dashUrl('tracking');
const trackingUrls = {
	trackUrls: `${trackingPrefix}/urls/frekuent`,
};

/* Chat */
const chatWSPrefix = wsUrl();
const chatWsUrl = {
	sendMessageCustomerSupport: (ticketId: string) =>
		`${chatWSPrefix}customer-service/chat/${ticketId}`,
};

/* ALerts */
const alertsPrefix = dashboardUrls.alerts;
const alertsUrls = {
	alertsTable: `${alertsPrefix}/table/received-alerts`,
	alertsConfigTable: `${alertsPrefix}/table/configuration-alerts`,
	alertDetails: (alertId: string) => `${alertsPrefix}/details?alert_id=${alertId}`,
	alertBoxUnresolvedAlerts: `${alertsPrefix}/box/unresolved-alerts`,
	alertBoxResolvedAlerts: `${alertsPrefix}/box/resolved-alerts`,
	createAlert: `${alertsPrefix}/alerts`,
	getAlertConfig: (configId: string) =>
		`${alertsPrefix}/alerts/configuration/${configId}?configuration_id=${configId}`,
	updateAlertConfig: (configId: string) =>
		`${alertsPrefix}/alerts/${configId}?configuration_id=${configId}`,
	deleteAlertConfig: `${alertsPrefix}/alerts`,
	resolveAlert: (eventId: string) => `${alertsPrefix}/events/resolve/${eventId}`,
	alertHistory: `${alertsPrefix}/table/machine-without-sales`,
};

/* Home */
const homeUrls = {
	newsBanner: 'marketing/banners',
} as const;

/* POS Personalization */
const posPersonalizationPrefix = dashboardUrls.pointOfSales;
const posPersonalizationUrls = {
	webScraping: `${posPersonalizationPrefix}/web-scraping`,
	togglePersonalization: `${posPersonalizationPrefix}/configuration/toggle`,
	crudPosPersonalization: (personalizationId: string) =>
		`${posPersonalizationPrefix}/pos-configurations${personalizationId ? `/${personalizationId}` : ''}`,
	uploadLogo: `${posPersonalizationPrefix}/logo`,
	posConfigurationsTable: `${posPersonalizationPrefix}/table/pos-configurations`,
	posConfigurationsByStatus: `${posPersonalizationPrefix}/pos-configurations`,
	getSpotPersonalization: `${posPersonalizationPrefix}/configuration`,
	deactivatePersonalization: `${posPersonalizationPrefix}/configuration/deactivate`,
	sendPdfPerMail: `${posPersonalizationPrefix}/send-mail`,
	machinesHaveConfigurations: `${posPersonalizationPrefix}/machines-has-configurations`,
	reinitializeMachines: `${posPersonalizationPrefix}/configuration/reinitialize`,
};

/* Addons */
const addonsPrefix = dashboardUrls.addons;
const addonsUrls = {
	requestInformation: `${addonsPrefix}/opportunity`,
	addonsList: `${addonsPrefix}/list`,
};

/* Marketplace */
const marketplacePrefix = dashboardUrls.marketplace;
const marketplaceUrls = {
	sendInterest: `${marketplacePrefix}/device-interest`,
};

const urls = {
	base: dashUrl(),
	baseDashboard: dashUrl(),
	userAuditLog: dashUrl('/user-audit-log'),
	auth: authUrls,
	finance: financeUrls,
	dashboard: dashboardUrls,
	boxes: getBoxUrl,
	customerService: customerServiceUrls,
	clients: clientsUrls,
	pointsOfSale: pointsOfSaleUrls,
	settings: settingsUrls,
	profile: profileUrls,
	marketing: marketingUrls,
	users: usersUrls,
	notifications: notificationsUrls,
	ws: wsHost,
	formOptions: formOptionsUrls,
	irp: irpUrls,
	reports: reportsUrls,
	tags: tagsUrls,
	tasks: tasksUrls,
	tracking: trackingUrls,
	chatWs: chatWsUrl,
	alerts: alertsUrls,
	orain: orainUrls,
	posPersonalization: posPersonalizationUrls,
	home: homeUrls,
	addons: addonsUrls,
	marketplace: marketplaceUrls,
} as const;

export default urls;
